import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Users, Plus, Loader2, Trash2, Mail } from "lucide-react";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;
const SUPPORTED_CURRENCIES = ["GBP", "USD", "NGN", "EUR"];

function FacultyForm({ initial, onSubmit, onCancel, submitting }) {
  const [draft, setDraft] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    type: initial?.type || "associate",
    bio_md: initial?.bio_md || "",
    regions: (initial?.regions || []).join(", "),
    specialisms: (initial?.specialisms || []).join(", "),
    active: initial?.active ?? true,
    rates: SUPPORTED_CURRENCIES.map((c) => {
      const existing = (initial?.rates || []).find((r) => r.currency === c);
      return { currency: c, day_rate: existing?.day_rate ?? "", notes: existing?.notes || "" };
    }),
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Name *</Label>
          <Input data-testid="faculty-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Type</Label>
          <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
            <option value="internal">Internal</option>
            <option value="associate">Associate</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Email</Label>
          <Input data-testid="faculty-email-input" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Active</Label>
          <select value={draft.active ? "true" : "false"} onChange={(e) => setDraft({ ...draft, active: e.target.value === "true" })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Regions (comma-separated)</Label>
          <Input value={draft.regions} onChange={(e) => setDraft({ ...draft, regions: e.target.value })} placeholder="UK, NG, RoA" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Specialisms (comma-separated)</Label>
          <Input value={draft.specialisms} onChange={(e) => setDraft({ ...draft, specialisms: e.target.value })} placeholder="Strategy, Leadership" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Bio</Label>
        <Textarea rows={3} value={draft.bio_md} onChange={(e) => setDraft({ ...draft, bio_md: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs text-slate-600 mb-1 block">Rate cards (day rate by currency)</Label>
        <div className="grid grid-cols-4 gap-2">
          {draft.rates.map((r, i) => (
            <div key={r.currency}>
              <Label className="text-[11px] text-slate-500">{r.currency}</Label>
              <Input
                type="number"
                value={r.day_rate}
                data-testid={`faculty-rate-${r.currency}`}
                onChange={(e) => {
                  const newRates = [...draft.rates];
                  newRates[i] = { ...newRates[i], day_rate: e.target.value };
                  setDraft({ ...draft, rates: newRates });
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          data-testid="faculty-submit-btn"
          disabled={submitting}
          onClick={() => {
            if (!draft.name?.trim()) return toast.error("Name required");
            const body = {
              name: draft.name.trim(),
              email: draft.email.trim() || null,
              type: draft.type,
              bio_md: draft.bio_md.trim() || null,
              regions: (draft.regions || "").split(",").map((s) => s.trim()).filter(Boolean),
              specialisms: (draft.specialisms || "").split(",").map((s) => s.trim()).filter(Boolean),
              active: draft.active,
              rates: draft.rates
                .filter((r) => r.day_rate !== "" && r.day_rate != null)
                .map((r) => ({ currency: r.currency, day_rate: parseFloat(r.day_rate), notes: r.notes || null })),
            };
            onSubmit(body);
          }}
        >
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function FacultyTab({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/faculty?include_inactive=true`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (body) => {
    setSubmitting(true);
    try {
      let r;
      if (editing && editing !== "new") {
        // update + replace rates separately
        r = await fetch(`${API}/v2/workspaces/${workspaceId}/faculty/${editing.faculty_id}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (r.ok && body.rates) {
          await fetch(`${API}/v2/workspaces/${workspaceId}/faculty/${editing.faculty_id}/rates`, {
            method: "PUT", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body.rates),
          });
        }
      } else {
        r = await fetch(`${API}/v2/workspaces/${workspaceId}/faculty`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      toast.success(editing && editing !== "new" ? "Faculty updated" : "Faculty added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (f) => {
    if (!confirm(`Delete ${f.name}?`)) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/faculty/${f.faculty_id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Delete failed");
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="faculty-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Internal staff and external associates with day-rate cards by currency.</p>
        <Button onClick={() => setEditing("new")} data-testid="faculty-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New faculty
        </Button>
      </div>

      {loading ? (
        <Card className="border-slate-200"><CardContent className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="faculty-empty"><Users className="h-8 w-8 mx-auto text-slate-300 mb-2" />No faculty yet — add your first associate or internal lead.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((f) => (
            <Card key={f.faculty_id} className={`border-slate-200 ${!f.active ? "opacity-60" : ""}`} data-testid={`faculty-card-${f.faculty_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-800 text-sm">{f.name}</h3>
                    {f.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="h-3 w-3" />{f.email}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" className="text-xs capitalize">{f.type}</Badge>
                    {!f.active && <Badge variant="outline" className="text-xs bg-slate-100">Inactive</Badge>}
                  </div>
                </div>
                {(f.specialisms || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {f.specialisms.map((s, i) => (
                      <span key={i} className="text-[10px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">{s}</span>
                    ))}
                  </div>
                )}
                {(f.regions || []).length > 0 && <p className="text-xs text-slate-500">Regions: {f.regions.join(", ")}</p>}
                {(f.rates || []).length > 0 && (
                  <div className="flex flex-wrap gap-3 text-xs text-slate-700 pt-1 border-t border-slate-100">
                    {f.rates.map((r) => (
                      <span key={r.currency}><strong>{r.currency}</strong> {Number(r.day_rate).toLocaleString()} /day</span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(f)} data-testid={`faculty-edit-${f.faculty_id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(f)} className="ml-auto text-red-600" data-testid={`faculty-delete-${f.faculty_id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="faculty-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-blue-600" />{editing === "new" ? "New faculty" : "Edit faculty"}</DialogTitle>
            <DialogDescription>Day rates by currency drive engagement margin estimation.</DialogDescription>
          </DialogHeader>
          {editing && (
            <FacultyForm
              initial={editing === "new" ? null : editing}
              submitting={submitting}
              onCancel={() => setEditing(null)}
              onSubmit={submit}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
